import { useNavigate, useSearchParams } from 'react-router-dom';

import { AnalyticsDashboardView } from '../analytics/AnalyticsDashboardView';
import {
  analyticsDateRangeToDoorVisitDateRange,
  analyticsDateRangeToGraphQLPreset,
  analyticsDateRangeToSearchParams,
  parseAnalyticsDateRange,
  type AnalyticsDateRange,
} from '../analytics/analyticsDateRange';
import type { DonutOptionSelection } from '../analytics/DonutInsightCard';
import { PeriodSelect } from '../analytics/PeriodSelect';
import { useAnalyticsDashboard } from '../analytics/useAnalyticsDashboard';
import { DEFAULT_FILTERS, filtersToSearchParams } from '../filters/doorVisitFilters';
import { PageControlsBar } from '../PageControlsBar';
import { PageHeader } from '../PageHeader';

// Mirrors CUSTOM_SLOT_INSIGHT_PREFIX in the server's custom-slot-aggregation.util.ts.
const CUSTOM_SLOT_INSIGHT_PREFIX = 'customSlot:';

export const DashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateRange = parseAnalyticsDateRange(searchParams);
  const { state, reload } = useAnalyticsDashboard(analyticsDateRangeToGraphQLPreset(dateRange));
  const navigate = useNavigate();

  const handleDateRangeChange = (nextDateRange: AnalyticsDateRange): void => {
    setSearchParams(analyticsDateRangeToSearchParams(searchParams, nextDateRange), { replace: true });
  };

  const handleOptionSelect = ({ insightId, answerValue }: DonutOptionSelection): void => {
    const customSlotName = insightId.startsWith(CUSTOM_SLOT_INSIGHT_PREFIX)
      ? insightId.slice(CUSTOM_SLOT_INSIGHT_PREFIX.length)
      : null;
    if (!customSlotName || !answerValue) return;

    const params = filtersToSearchParams({
      ...DEFAULT_FILTERS,
      dateRange: analyticsDateRangeToDoorVisitDateRange(dateRange),
      customSlotName,
      customSlotValue: answerValue,
    });
    void navigate(`/door-visits?${params.toString()}`);
  };

  return (
    <>
      <PageHeader
        title="Analytics Dashboard"
        subtitle="Aggregated insights across all canvassing conversations."
      />
      <PageControlsBar label="Filters">
        <PeriodSelect value={dateRange} onChange={handleDateRangeChange} />
      </PageControlsBar>
      <AnalyticsDashboardView state={state} onRetry={reload} onOptionSelect={handleOptionSelect} />
    </>
  );
};
